import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/react';
import { DragDropProvider, useDragDropMonitor, useDroppable } from '@dnd-kit/react';
import type React from 'react';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './board.module.scss';
import { BoardCell } from './board-cell';
import { DraggableBoardTile } from './board-draggable-tile';
import { resolveLanding } from './board-landing';
import {
  anchorWithinBoard,
  computeCellsCovered,
  DIAGONAL_WORDS,
  DIRECTION_LABEL,
  directionCycle,
  directionStep,
  fitCellSize,
  getBoardLayout,
  getBonusesHit,
  MERGE_OWN_TILES,
  rotateDirection,
  tileClusters,
  validateTile,
} from './board-layout';
import { BoardMarkers } from './board-markers';
import { BoardTile } from './board-tile';
import { BoardTileFace } from './board-tile-face';
import type {
  BoardProps,
  CellCoord,
  CellOffset,
  Direction,
  DragState,
  DropCell,
  KeyboardDragState,
  TileData,
} from './board-types';
import { tilePointerSensors } from './drag-sensors';
import { TileClusterShape } from './tile-cluster';
import type { TileDropData } from './tile-drag-overlay';
import { TileDragOverlay } from './tile-drag-overlay';

/**
 * Wheel distance one turn of the tile costs. A mouse notch clears it outright; a trackpad's stream of
 * small deltas accumulates towards it, so a flick turns the tile a step or two rather than spinning it.
 */
const WHEEL_ROTATE_DELTA = 60;

/** How long after a drag ends a click on the tile is taken to be that drag's own, and ignored. */
const CLICK_AFTER_DRAG_MS = 250;

/**
 * `Board`'s full implementation. Always rendered as a descendant of *some* `DragDropProvider` — its own,
 * self-provided one when `standalone` (the default), or an ambient one supplied by a parent composing
 * `Board` alongside other draggables (e.g. `TileRack`) when `standalone={false}`. Wiring drag handlers
 * via `useDragDropMonitor` rather than as `DragDropProvider` props is what makes both cases work
 * unchanged.
 */
const BoardInner: React.FC<BoardProps> = ({
  shape,
  size = 11,
  boardWidth,
  cellSize: cellSizeProp = 26,
  gap = 1,
  padding = 7,
  tiles,
  bonuses,
  showBonuses = true,
  start,
  hotLetters,
  draggable = true,
  diagonals = DIAGONAL_WORDS,
  mergeOwnTiles = MERGE_OWN_TILES,
  dropId = 'board',
  onTilePickup,
  onTileHover,
  onTilePlace,
  onTileReturn,
  onTileRotate,
  validate,
  incomingTile,
  ariaLabel = 'Game board',
}) => {
  const boardRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [keyboardDrag, setKeyboardDrag] = useState<KeyboardDragState | null>(null);
  const [announcement, setAnnouncement] = useState('');
  /**
   * Which character of the dragged tile the pointer grabbed. An index rather than a row/column
   * offset because the tile can be turned mid-drag: the offset a grabbed character sits at is a
   * property of the direction, and re-deriving it from the index is what lets the tile pivot about
   * the cell under the pointer instead of jumping when it turns.
   */
  const grabIndexRef = useRef(0);
  /**
   * The live drag state, and what every drag handler reads — `dragState` itself is for rendering.
   *
   * dnd-kit dispatches `dragstart` only once React has rendered, but `dragend` synchronously, while
   * `useDragDropMonitor` re-subscribes in a passive effect. A release that follows the pickup closely
   * — a click held just past the sensor's activation delay — therefore reaches the listener bound
   * *before* the pickup rendered, whose `dragState` is still null. Reading React state there would
   * drop the drag end and leave the tile ghosted at its origin with no way back.
   */
  const dragStateRef = useRef<DragState | null>(null);
  /** When the last pointer drag ended — a click fires straight after one, and must not also rotate. */
  const dragEndedAtRef = useRef(0);

  /** Moves the drag on, keeping the ref the handlers read in step with the state the board renders. */
  const applyDragState = useCallback((state: DragState | null) => {
    dragStateRef.current = state;
    setDragState(state);
  }, []);

  const cellSize = boardWidth != null ? fitCellSize({ boardWidth, size, gap, padding }) : cellSizeProp;

  // A start the game places replaces the layout's `★`, which would otherwise mark a second start.
  const cells = useMemo(
    () =>
      getBoardLayout(shape, size, bonuses).map((cell) =>
        start && cell.bonus === '★' ? { ...cell, bonus: null } : cell,
      ),
    [shape, size, bonuses, start],
  );
  const totalSize = size * (cellSize + gap) - gap + padding * 2;

  /**
   * The cell under the pointer, with the pointer's own position inside it — measured from the cell's
   * centre, so it says which way a drop on that cell leans. Rounding to a cell throws that away, and
   * it is what decides which side a tile slides out to when the cell it lands on is already taken.
   */
  const computeTargetCell = useCallback(
    (pointerX: number, pointerY: number): DropCell | null => {
      const board = boardRef.current;
      if (!board) return null;
      const rect = board.getBoundingClientRect();
      const x = (pointerX - rect.left - padding) / (cellSize + gap);
      const y = (pointerY - rect.top - padding) / (cellSize + gap);
      const col = Math.floor(x);
      const row = Math.floor(y);
      return { row, col, offset: { row: y - row - 0.5, col: x - col - 0.5 } };
    },
    [cellSize, gap, padding],
  );

  /** Where in a tile the grabbed character sits, clamped to a tile shorter than the last one grabbed. */
  const grabIndexFor = useCallback(
    (tile: TileData): number => Math.min(grabIndexRef.current, Math.max(tile.letter.length, 1) - 1),
    [],
  );

  /** The anchor cell the dragged tile would land on, keeping the grabbed character under the pointer. */
  const computeTargetAnchor = useCallback(
    (pointerX: number, pointerY: number, tile: TileData, dir: Direction): DropCell | null => {
      const cell = computeTargetCell(pointerX, pointerY);
      if (!cell) return null;
      const step = directionStep(dir);
      const index = grabIndexFor(tile);
      return { row: cell.row - step.dr * index, col: cell.col - step.dc * index, offset: cell.offset };
    },
    [computeTargetCell, grabIndexFor],
  );

  /**
   * The drag's target cell and where the tile would settle from it. A cell another tile already holds
   * does not sink the drop: the tile slides clear of what is in the way, and the state reports that
   * landing as the valid one — so the board highlights where the tile will actually come to rest
   * rather than the cell the pointer is over, and the release that follows can only agree with it.
   */
  const buildDragState = useCallback(
    (tile: TileData, targetRow: number, targetCol: number, dir: Direction, offset: CellOffset): DragState => {
      const candidate: TileData = { ...tile, row: targetRow, col: targetCol, dir };
      const result = validateTile(candidate, cells, tiles, size, validate, diagonals);
      const landing =
        result.reason === 'collision'
          ? resolveLanding({ tile: candidate, offset, cells, boardTiles: tiles, size, validate, diagonals })
          : null;

      return {
        tileId: tile.id,
        targetRow,
        targetCol,
        landingRow: landing?.row ?? targetRow,
        landingCol: landing?.col ?? targetCol,
        offset,
        dir,
        valid: landing ? true : result.valid,
        reason: landing ? undefined : result.reason,
      };
    },
    [cells, tiles, size, validate, diagonals],
  );

  // A droppable region covering the board, so an ambient DragDropProvider shared with another
  // draggable source (e.g. TileRack, when `standalone={false}`) can tell a drop landed here. Board's
  // own tiles never consult this — their placement is pointer-math against `boardRef`, computed below —
  // so registering it has no effect on in-board dragging.
  const { ref: setDropRef } = useDroppable<TileDropData>({ id: dropId, data: { region: dropId } });
  const setBoardRef = useCallback(
    (node: HTMLDivElement | null) => {
      boardRef.current = node;
      setDropRef(node);
    },
    [setDropRef],
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const tile = tiles.find((t) => t.id === event.operation.source?.id);
      if (!tile) return;

      // Where the gesture started, in viewport coordinates — dnd-kit's own record of the pointer that
      // began the drag, which beats reading the activator event: that is whichever event satisfied the
      // activation constraint, and so can sit a few pixels off the original press.
      const start = event.operation.position.initial;

      // Which character was grabbed. A press that lands just off the tile's cells — a pointer a
      // fraction outside its box — finds none, and is treated as a grab on the first.
      const grabbed = computeTargetCell(start.x, start.y);
      const covered = computeCellsCovered(tile);
      const index = grabbed ? covered.findIndex((c) => c.row === grabbed.row && c.col === grabbed.col) : -1;
      grabIndexRef.current = Math.max(0, index);

      // Centred: the tile starts on its own cell, which nothing else can be occupying, so there is no
      // lean to read until the first move puts it somewhere it might have to slide out of.
      const state = buildDragState(tile, tile.row, tile.col, tile.dir, { row: 0, col: 0 });
      applyDragState(state);
      setAnnouncement(`Picked up tile ${tile.letter}, use arrow keys to move`);
      onTilePickup?.(tile);
    },
    [tiles, computeTargetCell, buildDragState, applyDragState, onTilePickup],
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const current = dragStateRef.current;
      if (!current) return;
      const tile = tiles.find((t) => t.id === current.tileId);
      if (!tile) return;

      // `to` is where this move is going. dnd-kit dispatches `dragmove` *before* committing the new
      // coordinates to the operation, so `operation.position` is still one move behind here — reading
      // it instead would leave the board's highlight trailing the pointer by a move.
      const pointer = event.to ?? event.operation.position.current;
      const target = computeTargetAnchor(pointer.x, pointer.y, tile, current.dir);
      if (!target) return;

      // Built on every move rather than only on a change of cell, because where the tile settles is
      // not a property of the cell alone: over one that is taken, crossing the middle of that cell
      // sends the tile out the other side. The comparison below is what keeps a move that changes
      // neither the cell nor the landing from costing a render.
      const state = buildDragState(tile, target.row, target.col, current.dir, target.offset);
      const unchanged =
        state.targetRow === current.targetRow &&
        state.targetCol === current.targetCol &&
        state.landingRow === current.landingRow &&
        state.landingCol === current.landingCol;
      if (unchanged) return;
      applyDragState(state);

      const candidate: TileData = { ...tile, row: state.landingRow, col: state.landingCol, dir: current.dir };
      onTileHover?.({ tile: candidate, valid: state.valid, reason: state.reason });
    },
    [tiles, computeTargetAnchor, buildDragState, applyDragState, onTileHover],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      dragEndedAtRef.current = Date.now();
      const current = dragStateRef.current;
      if (!current) return;
      const tile = tiles.find((t) => t.id === current.tileId);
      if (!tile) {
        applyDragState(null);
        return;
      }

      // A cancelled drag — Escape, or the browser taking the pointer away — arrives as a drag end
      // flagged `canceled`, and always returns the tile, whatever the pointer's last position said.
      if (event.canceled) {
        onTileReturn?.(tile);
        setAnnouncement(`Tile ${tile.letter} returned`);
        applyDragState(null);
        return;
      }

      // By drag end the last move has been committed, so the operation's own position is the pointer's
      // final resting place — no `to` on this event to read instead.
      const pointer = event.operation.position.current;
      const target = computeTargetAnchor(pointer.x, pointer.y, tile, current.dir);

      const isOffBoard = !target || target.row < 0 || target.row >= size || target.col < 0 || target.col >= size;

      if (isOffBoard || !current.valid) {
        onTileReturn?.(tile);
        setAnnouncement(`Tile ${tile.letter} returned`);
      } else {
        // The landing, not the target: released over a cell another tile holds, the tile settles
        // beside it rather than on it — the same cell the board has been highlighting all along.
        const candidate: TileData = {
          ...tile,
          row: current.landingRow,
          col: current.landingCol,
          dir: current.dir,
        };
        const covered = computeCellsCovered(candidate);
        const bonusesHit = getBonusesHit(covered, cells);
        onTilePlace?.({ tile: candidate, cellsCovered: covered, bonusesHit, valid: true });
        setAnnouncement(
          `Tile ${tile.letter} placed at row ${current.landingRow + 1}, column ${current.landingCol + 1}`,
        );
      }

      applyDragState(null);
    },
    [tiles, computeTargetAnchor, applyDragState, cells, size, onTileReturn, onTilePlace],
  );

  const dragging = dragState !== null;

  /**
   * Turns the tile in the air, pivoting about the cell under the pointer rather than about its
   * anchor: the grabbed character stays put and the rest of the run swings around it, which is also
   * the cell the next drag move re-anchors from, so the tile does not jump on the following frame.
   */
  const rotateDrag = useCallback(
    (steps: number) => {
      const current = dragStateRef.current;
      if (!current) return;
      const tile = tiles.find((t) => t.id === current.tileId);
      if (!tile) return;

      const dir = rotateDirection(current.dir, steps, diagonals);
      if (dir === current.dir) return;

      const index = grabIndexFor(tile);
      const from = directionStep(current.dir);
      const to = directionStep(dir);
      const row = current.targetRow + (from.dr - to.dr) * index;
      const col = current.targetCol + (from.dc - to.dc) * index;

      const state = buildDragState(tile, row, col, dir, current.offset);
      applyDragState(state);
      setAnnouncement(`Tile ${tile.letter} turned ${DIRECTION_LABEL[dir]}`);
      onTileHover?.({
        tile: { ...tile, row: state.landingRow, col: state.landingCol, dir },
        valid: state.valid,
        reason: state.reason,
      });
    },
    [tiles, diagonals, grabIndexFor, buildDragState, applyDragState, onTileHover],
  );

  /**
   * The three pointer-drag rotation gestures, bound for the life of a drag only. All three are
   * window-level because the pointer is captured by the drag sensor for the duration, so nothing
   * reaches the tile's own node:
   *
   * - the wheel, the placement-game convention, accumulated so a trackpad's stream of small deltas
   *   turns the tile once per notch rather than spinning it;
   * - `R`, matching the keyboard drag, which has had it all along;
   * - a second finger touching down, the touch counterpart — one finger holds the tile, a tap
   *   anywhere else turns it, which the drag sensor never sees as movement.
   */
  useEffect(() => {
    if (!dragging) return;

    let wheelDelta = 0;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      wheelDelta += event.deltaY;
      const steps = Math.trunc(wheelDelta / WHEEL_ROTATE_DELTA);
      if (steps === 0) return;
      wheelDelta -= steps * WHEEL_ROTATE_DELTA;
      rotateDrag(steps);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'r' && event.key !== 'R') return;
      event.preventDefault();
      rotateDrag(1);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.isPrimary) return;
      rotateDrag(1);
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [dragging, rotateDrag]);

  /**
   * Turns a tile that is sitting on the board — one this turn put there, never a committed one.
   * Board does not own those tiles, so the turn is reported rather than applied; the anchor is pulled
   * back onto the grid first, because turning a three-letter tile onto `ur` lifts its far end two
   * rows and would otherwise run it off the top.
   */
  const rotateTileAtRest = useCallback(
    (source: TileData) => {
      if (!draggable || !onTileRotate || source.placed) return;
      const tile = tiles.find((t) => t.id === source.id) ?? source;
      const dir = directionCycle(tile.dir, diagonals);
      if (dir === tile.dir) return;
      const anchor = anchorWithinBoard({ ...tile, dir }, size);
      onTileRotate({ ...tile, row: anchor.row, col: anchor.col, dir });
      setAnnouncement(`Tile ${tile.letter} turned ${DIRECTION_LABEL[dir]}`);
    },
    [draggable, onTileRotate, tiles, diagonals, size],
  );

  /**
   * A tap or click turns a tile at rest. The pointer sensors leave a press-and-release free to be a
   * tap — that is what their activation delay buys — but a drag ends in a pointerup on the tile too,
   * and the click that follows it must not turn what was just put down.
   */
  const handleTileClick = useCallback(
    (_e: React.MouseEvent, tile: TileData) => {
      if (Date.now() - dragEndedAtRef.current < CLICK_AFTER_DRAG_MS) return;
      rotateTileAtRest(tile);
    },
    [rotateTileAtRest],
  );

  const handleTileKeyDown = useCallback(
    (e: React.KeyboardEvent, source: TileData) => {
      if (!draggable) return;

      // The rendered tile may carry presentation-only flags (e.g. the origin ghost); always work
      // from — and hand back to the consumer — the canonical tile from `tiles`.
      const tile = tiles.find((t) => t.id === source.id) ?? source;

      if ((e.key === ' ' || e.key === 'Enter') && !keyboardDrag) {
        e.preventDefault();
        setKeyboardDrag({ tileId: tile.id, row: tile.row, col: tile.col, dir: tile.dir });
        setAnnouncement(`Picked up tile ${tile.letter}, use arrow keys to move`);
        onTilePickup?.(tile);
        return;
      }

      // `R` on a focused tile with nothing in the air turns it where it stands — the keyboard
      // counterpart of a tap, and the same key that turns a tile mid-drag.
      if ((e.key === 'r' || e.key === 'R') && !keyboardDrag) {
        e.preventDefault();
        rotateTileAtRest(tile);
        return;
      }

      if (!keyboardDrag || keyboardDrag.tileId !== tile.id) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onTileReturn?.(tile);
        setAnnouncement(`Tile ${tile.letter} returned`);
        setKeyboardDrag(null);
        return;
      }

      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        const dir = directionCycle(keyboardDrag.dir, diagonals);
        const anchor = anchorWithinBoard({ ...tile, row: keyboardDrag.row, col: keyboardDrag.col, dir }, size);
        setKeyboardDrag({ ...keyboardDrag, row: anchor.row, col: anchor.col, dir });
        setAnnouncement(`Tile ${tile.letter} turned ${DIRECTION_LABEL[dir]}`);
        return;
      }

      let dr = 0;
      let dc = 0;
      if (e.key === 'ArrowUp') dr = -1;
      else if (e.key === 'ArrowDown') dr = 1;
      else if (e.key === 'ArrowLeft') dc = -1;
      else if (e.key === 'ArrowRight') dc = 1;
      else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        const candidate: TileData = { ...tile, row: keyboardDrag.row, col: keyboardDrag.col, dir: keyboardDrag.dir };
        const result = validateTile(candidate, cells, tiles, size, validate, diagonals);
        if (result.valid) {
          const covered = computeCellsCovered(candidate);
          const bonusesHit = getBonusesHit(covered, cells);
          onTilePlace?.({ tile: candidate, cellsCovered: covered, bonusesHit, valid: true });
          setAnnouncement(`Tile ${tile.letter} placed at row ${keyboardDrag.row + 1}, column ${keyboardDrag.col + 1}`);
        } else {
          onTileReturn?.(tile);
          setAnnouncement(`Cannot place tile: ${result.reason}`);
        }
        setKeyboardDrag(null);
        return;
      } else {
        return;
      }

      e.preventDefault();
      const newRow = Math.max(0, Math.min(size - 1, keyboardDrag.row + dr));
      const newCol = Math.max(0, Math.min(size - 1, keyboardDrag.col + dc));
      setKeyboardDrag({ ...keyboardDrag, row: newRow, col: newCol });

      const candidate: TileData = { ...tile, row: newRow, col: newCol, dir: keyboardDrag.dir };
      const result = validateTile(candidate, cells, tiles, size, validate, diagonals);
      const covered = computeCellsCovered(candidate);
      const cellLabels = covered
        .map((c: CellCoord) => {
          const cell = cells.find((x) => x.row === c.row && x.col === c.col);
          return cell?.bonus
            ? `row ${c.row + 1}, column ${c.col + 1} — ${cell.bonus}`
            : `row ${c.row + 1}, column ${c.col + 1}`;
        })
        .join('; ');
      setAnnouncement(result.valid ? cellLabels : `Invalid: ${result.reason}`);
      onTileHover?.({ tile: candidate, valid: result.valid, reason: result.reason });
    },
    [
      draggable,
      keyboardDrag,
      onTilePickup,
      onTileReturn,
      onTilePlace,
      onTileHover,
      cells,
      tiles,
      size,
      validate,
      diagonals,
      rotateTileAtRest,
    ],
  );

  const coveredCells = useMemo<Map<string, 'valid' | 'invalid'>>(() => {
    const map = new Map<string, 'valid' | 'invalid'>();
    // A keyboard drag has no landing of its own: arrow keys put the tile on an exact cell, and an
    // occupied one is refused rather than slid out of — the player chose it outright.
    const activeDrag =
      dragState ??
      (keyboardDrag
        ? {
            tileId: keyboardDrag.tileId,
            landingRow: keyboardDrag.row,
            landingCol: keyboardDrag.col,
            dir: keyboardDrag.dir,
          }
        : null);
    const tile = activeDrag ? tiles.find((t) => t.id === activeDrag.tileId) : null;
    const candidate: TileData | null =
      activeDrag && tile
        ? { ...tile, row: activeDrag.landingRow, col: activeDrag.landingCol, dir: activeDrag.dir }
        : (incomingTile ?? null);
    if (!candidate) return map;

    const result = validateTile(candidate, cells, tiles, size, validate, diagonals);
    const covered = computeCellsCovered(candidate);
    const color: 'valid' | 'invalid' = result.valid ? 'valid' : 'invalid';
    for (const c of covered) {
      map.set(`${c.row},${c.col}`, color);
    }
    return map;
  }, [dragState, keyboardDrag, incomingTile, tiles, cells, size, validate, diagonals]);

  const draggedTileId = dragState?.tileId ?? keyboardDrag?.tileId ?? null;

  const draggedTile = useMemo(
    () => (draggedTileId ? (tiles.find((t) => t.id === draggedTileId) ?? null) : null),
    [draggedTileId, tiles],
  );

  /**
   * Clusters of touching tiles, each drawn as one shape. The tile in the air is left out, so lifting a
   * tile out of a cluster splits or shrinks the shape under it for the duration of the drag.
   */
  const clusters = useMemo(
    () =>
      tileClusters(
        tiles.filter((t) => t.id !== draggedTileId),
        { diagonals, mergeOwnTiles },
      ),
    [tiles, draggedTileId, diagonals, mergeOwnTiles],
  );

  /** Tiles drawn inside a cluster shape, which render their letters only. */
  const clusteredTiles = useMemo(
    () => new Set(clusters.flatMap((cluster) => cluster.tiles.map((t) => t.id))),
    [clusters],
  );

  const tileCoverageSet = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const tile of tiles) {
      // The dragged tile's origin cells are uncovered while it is in the air, so any bonus
      // underneath shows through the unfilled origin ghost.
      if (tile.id === draggedTileId) continue;
      for (const c of computeCellsCovered(tile)) {
        set.add(`${c.row},${c.col}`);
      }
    }
    return set;
  }, [tiles, draggedTileId]);

  /**
   * A keyboard drag has no DragOverlay to follow, so the moving tile is drawn at the current target
   * with the same lifted visuals. Without this a keyboard user would see an empty dashed box at the
   * origin, tinted cells at the target, and no letters anywhere.
   */
  const keyboardTile = useMemo<TileData | null>(
    () =>
      keyboardDrag && draggedTile
        ? { ...draggedTile, row: keyboardDrag.row, col: keyboardDrag.col, dir: keyboardDrag.dir }
        : null,
    [keyboardDrag, draggedTile],
  );

  // Registers against whichever DragDropProvider is actually mounted — Board's own (standalone) or an
  // ambient one a parent provides (standalone={false}) — so the handlers work identically either way.
  // Memoized so the subscription effect only re-runs when a handler itself changes, not on every
  // unrelated re-render — which would otherwise unsubscribe and resubscribe through a single gesture.
  const dndMonitorListeners = useMemo(
    () => ({
      onDragStart: handleDragStart,
      onDragMove: handleDragMove,
      onDragEnd: handleDragEnd,
    }),
    [handleDragStart, handleDragMove, handleDragEnd],
  );
  useDragDropMonitor(dndMonitorListeners);

  return (
    <div
      ref={setBoardRef}
      role="grid"
      aria-rowcount={size}
      aria-colcount={size}
      aria-label={ariaLabel}
      className={styles.board}
      style={{ '--board-size': `${totalSize}px` } as CSSProperties}
    >
      {/* Cell grid */}
      <div
        aria-hidden="true"
        className={styles.cellGrid}
        style={
          {
            '--cell-grid-left': `${padding}px`,
            '--cell-grid-top': `${padding}px`,
            '--cell-grid-cols': `repeat(${size}, ${cellSize}px)`,
            '--cell-grid-rows': `repeat(${size}, ${cellSize}px)`,
            '--cell-grid-gap': `${gap}px`,
          } as CSSProperties
        }
      >
        {cells.map((cell) => (
          <BoardCell
            key={`${cell.row}-${cell.col}`}
            cell={cell}
            cellSize={cellSize}
            showBonus={showBonuses}
            covered={tileCoverageSet.has(`${cell.row},${cell.col}`)}
            highlight={coveredCells.get(`${cell.row},${cell.col}`) ?? null}
          />
        ))}
      </div>

      {/* Start square and hot letters — over the grid, under the tiles that cover them */}
      {(start || (hotLetters?.length ?? 0) > 0) && (
        <BoardMarkers
          start={start}
          hotLetters={hotLetters ?? []}
          covered={tileCoverageSet}
          cellSize={cellSize}
          gap={gap}
          padding={padding}
        />
      )}

      {/* Cluster shapes — under the tile layer, which contributes only the letters */}
      {clusters.map((cluster) => (
        <TileClusterShape
          key={`cluster-${cluster.tiles[0].id}`}
          cluster={cluster}
          cellSize={cellSize}
          gap={gap}
          padding={padding}
        />
      ))}

      {/* Tile layer */}
      {tiles.map((tile) => {
        const isBeingDragged = tile.id === draggedTileId;
        const isMoveTarget = draggable && !tile.placed;
        // The origin node stays mounted and in place — dnd-kit measures it for the drop
        // animation — but presents as the dashed origin ghost while the tile is in the air.
        // A draggable tile ghosts itself, keeping the canonical tile for the overlay to fly with.
        const rendered = isBeingDragged ? { ...tile, ghost: true } : tile;
        const bare = clusteredTiles.has(tile.id);

        if (isMoveTarget) {
          return (
            <DraggableBoardTile
              key={tile.id}
              tile={tile}
              cellSize={cellSize}
              gap={gap}
              padding={padding}
              isDragging={isBeingDragged}
              dragDir={isBeingDragged ? dragState?.dir : undefined}
              region={dropId}
              bare={bare}
              onKeyDown={handleTileKeyDown}
              onClick={handleTileClick}
            />
          );
        }

        return (
          <BoardTile
            key={tile.id}
            tile={rendered}
            cellSize={cellSize}
            gap={gap}
            padding={padding}
            isDragging={isBeingDragged}
            bare={bare}
            onKeyDown={handleTileKeyDown}
          />
        );
      })}

      {/* Keyboard drag — the moving tile, drawn at the current target */}
      {keyboardTile && (
        <BoardTileFace
          aria-hidden="true"
          data-keyboard-drag-tile={keyboardTile.id}
          tile={keyboardTile}
          cellSize={cellSize}
          gap={gap}
          padding={padding}
          lifted
        />
      )}

      {/* Aria live region */}
      <div role="status" aria-live="polite" className={styles.visuallyHidden}>
        {announcement}
      </div>
    </div>
  );
};

/**
 * The game board: grid, tiles, and drag layer.
 *
 * By default (`standalone`, the default) Board provisions its own `DragDropProvider`, sensors and drag
 * overlay — fully self-contained. Passing `standalone={false}` skips that provisioning so Board can be
 * composed as one of several draggable regions under a `DragDropProvider` a parent screen owns — e.g.
 * alongside `TileRack`, to drag a tile from the rack onto the board. In that mode the parent supplies
 * the sensors — pass the exported `tilePointerSensors` for the same activation behaviour — and renders
 * a single `TileDragOverlay` of its own, which is what draws any tile in the air, whichever region it
 * came from.
 */
export const Board: React.FC<BoardProps> = ({ standalone = true, ...props }) => {
  if (!standalone) return <BoardInner {...props} />;

  return (
    <DragDropProvider sensors={tilePointerSensors}>
      <BoardInner {...props} />
      <TileDragOverlay />
    </DragDropProvider>
  );
};
